import { useEffect, useState } from 'react'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Container,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  VStack,
  Badge,
} from '@chakra-ui/react'
import { LuEye, LuEyeOff, LuLock, LuUser, LuMail, LuShieldCheck, LuArrowRight, LuChevronDown, LuChevronUp } from 'react-icons/lu'
import { useParams } from 'react-router-dom'

import { publicAxios } from '../../api/axios'
import { CHROME_HEADER_GRADIENT } from '../../theme/chrome'
import BlotGallery from '../../components/Lab/BlotAnalysisWorkspace/components/BlotGallery'
import { resolvePathologyLabel } from '../../components/Lab/BlotAnalysisWorkspace/ImmunoBlotSelection'

/**
 * Read-only "Image Source" panel for the guest viewer — the full plate scan
 * with the same detection-box overlay as the authenticated workspace
 * (index.tsx's showBlotBoxes section), minus everything interactive
 * (dragging, rotation, re-analysis, control-line markers): a guest can only
 * ever look, never edit. Fetches the image itself from the new
 * /blot-case-guest/case/:id/source-image endpoint (guest-token gated,
 * resolved server-side from the case's own fileUrl only).
 */
function GuestSourceImagePanel({ caseId, guestToken, analysis }: { caseId: string; guestToken: string; analysis: any }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [showBoxes, setShowBoxes] = useState(true)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await publicAxios.get(`blot-case-guest/case/${caseId}/source-image`, {
          params: { token: guestToken },
          responseType: 'blob',
        })
        if (cancelled) return
        objectUrl = URL.createObjectURL(data)
        setImageUrl(objectUrl)
      } catch {
        if (!cancelled) setImageError(true)
      }
    })()
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [caseId, guestToken])

  const hiddenIds: number[] = analysis.hiddenBlotIds ?? []
  const blots: any[] = (analysis.blots ?? []).filter((b: any) => !hiddenIds.includes(b.id))
  const roi = analysis.roi

  return (
    <Box mb={5}>
      <HStack spacing={2.5} mb={3}>
        <Box w="4px" h="22px" borderRadius="full" bgGradient="linear(to-b, teal.400, cyan.400)" />
        <Text fontWeight="bold" color="gray.800" fontSize="lg" letterSpacing="-0.01em">
          Image source
        </Text>
        {analysis.filename && (
          <Text fontSize="sm" color="gray.500" fontWeight="medium" isTruncated maxW={{ base: '130px', md: '260px', lg: '380px' }} title={analysis.filename}>
            {analysis.filename}
          </Text>
        )}
        <Button
          size="xs"
          variant="ghost"
          color="gray.600"
          rightIcon={<Icon as={isOpen ? LuChevronUp : LuChevronDown} boxSize={3.5} />}
          onClick={() => setIsOpen((v) => !v)}
          flexShrink={0}
        >
          {isOpen ? 'Masquer' : "Afficher l'image"}
        </Button>
      </HStack>

      {isOpen && (
        <Box bg="white" p={6} borderRadius="xl" shadow="lg" border="1px solid" borderColor="gray.100">
          {imageError ? (
            <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
              Image source indisponible.
            </Text>
          ) : !imageUrl ? (
            <Center py={10}><Spinner color="teal.500" /></Center>
          ) : (
            <>
              <HStack justify="flex-end" mb={3}>
                <Button
                  size="xs"
                  variant="outline"
                  colorScheme={showBoxes ? 'teal' : 'gray'}
                  leftIcon={<Icon as={showBoxes ? LuEye : LuEyeOff} boxSize={3.5} />}
                  onClick={() => setShowBoxes((v) => !v)}
                >
                  {showBoxes ? 'Bandelettes détectées' : 'Image brute'}
                </Button>
              </HStack>
              <Box position="relative">
                <Box
                  as="img"
                  src={imageUrl}
                  w="100%"
                  borderRadius="8px"
                  onLoad={(e: any) => setImageDimensions({ width: e.target.naturalWidth, height: e.target.naturalHeight })}
                />
                {showBoxes && imageDimensions && blots.map((blot) => {
                  const roiX = roi?.x || 0
                  const roiY = roi?.y || 0
                  const isPdf = analysis.filename?.toLowerCase().endsWith('.pdf')
                  const dpiScale = isPdf ? 300 / 72 : 1
                  const backendImgW = roi?.width > 0 ? roi.width : imageDimensions.width * dpiScale
                  const backendImgH = roi?.height > 0 ? roi.height : imageDimensions.height * dpiScale
                  const angle = blot.angle ?? 0
                  const obbW = blot.image_width || blot.x1 - blot.x0
                  const obbH = blot.image_height || blot.y_bottom - blot.y_top
                  const centerX = (blot.x0 + blot.x1) / 2
                  const centerY = (blot.y_top + blot.y_bottom) / 2
                  const leftPct = ((roiX + centerX - obbW / 2) / backendImgW) * 100
                  const topPct = ((roiY + centerY - obbH / 2) / backendImgH) * 100
                  const widthPct = (obbW / backendImgW) * 100
                  const heightPct = (obbH / backendImgH) * 100
                  return (
                    <Box
                      key={blot.id}
                      position="absolute"
                      left={`${leftPct}%`}
                      top={`${topPct}%`}
                      width={`${widthPct}%`}
                      height={`${heightPct}%`}
                      border="1.5px solid"
                      borderColor="cyan.400"
                      borderRadius="3px"
                      bg="rgba(0, 188, 212, 0.08)"
                      pointerEvents="none"
                      style={angle !== 0 ? { transformOrigin: 'center center', transform: `rotate(${angle}deg)` } : undefined}
                      title={blot.strip_id || `Blot ${blot.id}`}
                    />
                  )
                })}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  )
}

/**
 * Public, no-account viewer for a case shared via the "Partager le cas"
 * feature — the emailed `?shareToken=` link only lifts the "owner only"
 * restriction server-side, it still requires a full logged-in account by
 * default. This page is the light alternative: exchange the share token for
 * a scoped, read-only guest JWT after a minimal name/email identification
 * step (kept — not anonymous — so the access stays traceable in the case
 * owner's audit log, per the CHU pilot conventions' Article 3 quater), then
 * render the case with BlotGallery directly in readOnly mode.
 *
 * Route: /partage/:shareToken
 */
// A guest token is a 30-day JWT — remembering it (per share link) means a
// refresh, or reopening the same email link tomorrow, doesn't force the
// name/email prompt again. Scoped by shareToken (part of the URL) so two
// different share links never collide; wrapped in try/catch since
// localStorage can throw (private browsing, blocked storage).
const storageKey = (shareToken: string) => `careflai.guestAccess.${shareToken}`
const readStoredGuestToken = (shareToken: string): string | null => {
  try { return localStorage.getItem(storageKey(shareToken)) } catch { return null }
}
const writeStoredGuestToken = (shareToken: string, guestToken: string) => {
  try { localStorage.setItem(storageKey(shareToken), guestToken) } catch { /* ignore */ }
}
const clearStoredGuestToken = (shareToken: string) => {
  try { localStorage.removeItem(storageKey(shareToken)) } catch { /* ignore */ }
}

export default function GuestCaseAccess() {
  const { shareToken } = useParams<{ shareToken: string }>()
  const [step, setStep] = useState<'identify' | 'loading' | 'viewing' | 'error'>('identify')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [analysis, setAnalysis] = useState<any>(null)
  const [pathologyConfigMap, setPathologyConfigMap] = useState<Record<string, any>>({})
  const [guestToken, setGuestToken] = useState<string>('')

  const loadCase = async (guestToken: string, caseId: string) => {
    const { data: caseResponse } = await publicAxios.get(`blot-case-guest/case/${caseId}`, {
      params: { token: guestToken },
    })
    const caseAnalysis = caseResponse.analysis

    const labels: string[] = Array.from(
      new Set(
        (caseAnalysis.blots ?? [])
          .map((b: any) => b.pathology_label)
          .filter((l: any): l is string => !!l)
      )
    )
    const entries = await Promise.all(
      labels.map(async (label) => {
        const resolved = resolvePathologyLabel(label)
        if (!resolved) return [label, null] as const
        try {
          const { data } = await publicAxios.get('blot-case-guest/immunoblot-config', {
            params: { pathology: resolved.name, token: guestToken },
          })
          return [label, data.data?.[0] ?? null] as const
        } catch {
          return [label, null] as const
        }
      })
    )

    setAnalysis(caseAnalysis)
    setPathologyConfigMap(Object.fromEntries(entries))
    setGuestToken(guestToken)
    setStep('viewing')
  }

  // Refresh / reopen the same link: try the remembered guest token first,
  // skipping the identification form entirely. Falls back to it silently
  // if the token is gone, expired, or the share got revoked meanwhile.
  useEffect(() => {
    if (!shareToken) return
    const stored = readStoredGuestToken(shareToken)
    if (!stored) return
    let payloadCaseId: string
    try {
      payloadCaseId = JSON.parse(atob(stored.split('.')[1])).caseId
    } catch {
      clearStoredGuestToken(shareToken)
      return
    }
    setStep('loading')
    loadCase(stored, payloadCaseId).catch(() => {
      clearStoredGuestToken(shareToken)
      setStep('identify')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!shareToken) return
    setStep('loading')
    setErrorMessage('')
    try {
      const { data: exchange } = await publicAxios.post('blot-case-guest/guest-access', {
        shareToken,
        name,
        email,
      })
      const { guestToken, caseId } = exchange
      await loadCase(guestToken, caseId)
      writeStoredGuestToken(shareToken, guestToken)
    } catch (err: any) {
      setErrorMessage(
        err?.response?.data?.error === 'Invalid or revoked share link'
          ? "Ce lien de partage n'est plus valide ou a été révoqué."
          : "Impossible d'ouvrir ce cas. Vérifiez le lien reçu par e-mail."
      )
      setStep('error')
    }
  }

  if (step === 'viewing' && analysis) {
    return (
      <Box h="100dvh" overflowY="auto" bg="#F1EFEE">
        <Box bgGradient={CHROME_HEADER_GRADIENT} color="white" px={{ base: 4, md: 8 }} py={4}>
          <HStack justify="space-between">
            <HStack spacing={3}>
              <Icon as={LuEye} boxSize={5} />
              <Text fontWeight="700">Careflai — Consultation partagée</Text>
            </HStack>
            <Badge colorScheme="whiteAlpha" bg="whiteAlpha.300" px={3} py={1} borderRadius="full">
              <HStack spacing={1}><Icon as={LuLock} boxSize={3} /><Text fontSize="xs">Lecture seule</Text></HStack>
            </Badge>
          </HStack>
        </Box>
        <Box p={{ base: 3, md: 6 }}>
          <GuestSourceImagePanel caseId={analysis.id} guestToken={guestToken} analysis={analysis} />
          <BlotGallery
            blots={analysis.blots}
            caseId={analysis.id}
            pathologyConfigMap={pathologyConfigMap}
            manualBlotResults={analysis.manualBlotResults ?? {}}
            savedHiddenBlotIds={analysis.hiddenBlotIds ?? []}
            savedPositiveControlId={analysis.selectedPositiveControlId ?? null}
            savedPositiveControl2Id={analysis.selectedPositiveControl2Id ?? null}
            savedPositiveControl3Id={analysis.selectedPositiveControl3Id ?? null}
            savedPositiveControlsByPathology={analysis.positiveControlsByPathology ?? null}
            savedBandCalibration={analysis.bandCalibration ?? null}
            savedControlLineOverrides={analysis.controlLineOverrides ?? null}
            savedPlacedBands={analysis.placedBands ?? null}
            savedSuppressedBands={analysis.suppressedBands ?? null}
            aiAccessible
            readOnly
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box
      minH="100dvh"
      overflowY="auto"
      bgGradient="linear(160deg, #f1f5f9 0%, #eef2ff 45%, #f1EFEE 100%)"
      py={{ base: 8, md: 16 }}
      px={4}
    >
      <Container maxW="440px">
        <VStack spacing={5} align="stretch">
          {/* Brand mark, outside the card — echoes the login screen's split feel */}
          <VStack spacing={2}>
            <Box
              w="56px"
              h="56px"
              borderRadius="2xl"
              bgGradient={CHROME_HEADER_GRADIENT}
              display="grid"
              sx={{ placeItems: 'center' }}
              boxShadow="0 8px 24px rgba(13,148,136,0.28)"
            >
              <Icon as={LuEye} boxSize={6} color="white" />
            </Box>
            <Text fontSize="xs" fontWeight="700" letterSpacing="1.5px" textTransform="uppercase" color="teal.700">
              Careflai
            </Text>
          </VStack>

          <Box bg="white" borderRadius="2xl" boxShadow="0 20px 50px rgba(15,23,42,0.08)" borderWidth="1px" borderColor="gray.100" overflow="hidden">
            <Box px={8} pt={7} pb={5} textAlign="center">
              <Heading as="h1" fontSize="xl" fontWeight="800" color="gray.900" mb={1.5}>
                Cas partagé avec vous
              </Heading>
              <Text fontSize="sm" color="gray.500">
                Consultation en lecture seule — aucun compte requis
              </Text>
            </Box>

            <Box px={8} pb={8}>
              {step === 'loading' ? (
                <Center py={10}>
                  <VStack spacing={3}>
                    <Spinner color="teal.500" thickness="3px" />
                    <Text fontSize="sm" color="gray.600">Ouverture du cas…</Text>
                  </VStack>
                </Center>
              ) : (
                <form onSubmit={handleSubmit}>
                  <VStack align="stretch" spacing={4}>
                    {step === 'error' && (
                      <Alert status="error" borderRadius="lg" fontSize="sm">
                        <AlertIcon />
                        {errorMessage}
                      </Alert>
                    )}

                    <FormControl isRequired>
                      <FormLabel fontSize="sm" color="gray.700">Nom</FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon as={LuUser} color="gray.400" boxSize={4} />
                        </InputLeftElement>
                        <Input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Dr Jean Dupont"
                          borderRadius="lg"
                          bg="gray.50"
                          _focus={{ bg: 'white', borderColor: 'teal.400', boxShadow: '0 0 0 1px var(--chakra-colors-teal-400)' }}
                        />
                      </InputGroup>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontSize="sm" color="gray.700">E-mail</FormLabel>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <Icon as={LuMail} color="gray.400" boxSize={4} />
                        </InputLeftElement>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="vous@chu-exemple.fr"
                          borderRadius="lg"
                          bg="gray.50"
                          _focus={{ bg: 'white', borderColor: 'teal.400', boxShadow: '0 0 0 1px var(--chakra-colors-teal-400)' }}
                        />
                      </InputGroup>
                    </FormControl>

                    <Button
                      type="submit"
                      bgGradient={CHROME_HEADER_GRADIENT}
                      color="white"
                      _hover={{ opacity: 0.92, transform: 'translateY(-1px)' }}
                      _active={{ opacity: 0.85 }}
                      borderRadius="lg"
                      fontWeight="700"
                      size="lg"
                      rightIcon={<Icon as={LuArrowRight} boxSize={4} />}
                    >
                      Ouvrir le cas
                    </Button>

                    <HStack spacing={2} bg="gray.50" borderRadius="lg" px={3} py={2.5} align="start">
                      <Icon as={LuShieldCheck} color="teal.500" boxSize={4} mt={0.5} flexShrink={0} />
                      <Text fontSize="xs" color="gray.500" lineHeight="1.5">
                        Ces informations restent consultables par le propriétaire du cas dans son
                        journal d'accès. Aucune donnée n'est utilisée à d'autres fins.
                      </Text>
                    </HStack>
                  </VStack>
                </form>
              )}
            </Box>
          </Box>
        </VStack>
      </Container>
    </Box>
  )
}
